import Button from '@/components/Button';
import { Icon, IconSize, IconVariant } from '@/components/Icon';
import Logo from '@/components/Logo';
import { LogoVariant } from '@/components/LogoVariant';
import { PlayNumberConfig } from '@/config/game';
import classnames from '@/utilities/classnames';
import { splitWinningNumbers } from '@/utilities/formDrawVideoDescription';
import getRandomNumberArray from '@/utilities/getRandomNumberArray';
import pushDatalayer, { DataLayerEvent } from '@/utilities/pushDatalayer';
import { InputMask } from '@react-input/mask';
import React, { createRef } from 'react';

export interface PlayNumberLabelProps {
  title: string;
  logoCompact?: LogoVariant;
  logoColor?: LogoVariant;
  isActive: boolean;
  isDisabled?: boolean;
  digitAmount: number;
}
interface PlayNumberState {
  width?: number;
}

export interface PlayNumberProps extends PlayNumberConfig {
  onChange: (numbers: number[]) => void;
  selectedNumbers: number[];
  labels: PlayNumberLabelProps[];
}

export class PlayNumber extends React.Component<
  PlayNumberProps,
  PlayNumberState
> {
  private inputRef;
  static defaultProps: Partial<PlayNumberProps> = {
    superNumberIndex: -1,
  };
  constructor(props) {
    super(props);

    this.inputRef = createRef();
    this.state = {
      width: 0,
    };
  }

  onChange = ($event: React.FormEvent<HTMLInputElement>): void => {
    const value: string = $event.currentTarget.value;
    const numbers: number[] =
      $event.currentTarget.value === ''
        ? getRandomNumberArray(
            this.props.amountNumbers,
            this.props.valueMin,
            this.props.valueMax
          )
        : value.split('').map((n) => +n);
    this.props.onChange(numbers);
  };

  randomize = (): void => {
    pushDatalayer(
      `click_element_name`,
      `Losnummer - Zufall`,
      DataLayerEvent.UserClick
    );

    this.props.onChange(
      getRandomNumberArray(
        this.props.amountNumbers,
        this.props.valueMin,
        this.props.valueMax
      )
    );
  };

  private get numbersAsString(): string {
    return this.props.selectedNumbers.join('');
  }

  private get inputMask(): string {
    return '_'.repeat(this.props.amountNumbers);
  }

  private get calculateWidth(): number {
    return this.props.amountNumbers * 2.1;
  }

  private get style(): any {
    return { width: `${this.state.width}ch` };
  }

  componentDidMount() {
    this.setState({
      width: this.calculateWidth,
    });

    if (this.inputRef?.current?.focus)
      (this.inputRef.current as HTMLInputElement).focus();
  }

  render(): JSX.Element {
    const labels: PlayNumberLabelProps[][] = this.props.labels
      .reduce((previousValue: PlayNumberLabelProps[][], currentValue) => {
        if (currentValue.isDisabled) {
          return previousValue;
        }
        for (const item of previousValue) {
          const sameDigitAmount = item.find(
            (label) => label.digitAmount === currentValue.digitAmount
          );
          if (sameDigitAmount && !currentValue.isDisabled) {
            item.push(currentValue);
            return previousValue;
          }
        }
        return [...previousValue, [currentValue]];
      }, [])
      .sort((a, b) => a[0].digitAmount - b[0].digitAmount);

    return (
      <div className="PlayNumber">
        <div className="PlayNumber__container" aria-live="polite">
          <label
            htmlFor="playNumberInput"
            id="playNumberInputLabel"
            tabIndex={0}
            className="PlayNumber__input-label"
          >
            Losnummer
          </label>
          <InputMask
            id="playNumberInput"
            ref={this.inputRef}
            tabIndex={0}
            className="PlayNumber__input"
            mask={this.inputMask}
            replacement={{ _: /\d/ }}
            aria-label={`Losnummer ${splitWinningNumbers(
              this.numbersAsString
            )}`}
            onChange={this.onChange}
            onClick={() => {
              pushDatalayer(
                `click_element_name`,
                `Losnummer - Klick in die Losnummer`,
                DataLayerEvent.UserClick
              );
            }}
            value={this.numbersAsString}
            showMask={false}
            style={this.style}
          />

          {labels.map((label, index) => {
            const className = classnames('PlayNumber__label', {
              [`PlayNumber__label--${label[0].digitAmount}`]:
                !!label[0].digitAmount,
            });
            return (
              <div className={className} key={index}>
                <span className="PlayNumber__label-title">
                  {label.map((item, _index) => (
                    <React.Fragment key={_index}>
                      <span>
                        {item.logoColor ? (
                          <Logo
                            variant={
                              item.logoCompact
                                ? item.logoCompact
                                : item.logoColor
                            }
                            key={_index}
                            title={item.title}
                            className={classnames(
                              'PlayNumber__label-logo',
                              `PlayNumber__label-logo--${item.logoColor}`,
                              {
                                'PlayNumber__label-logo--disabled':
                                  !item.isActive,
                              }
                            )}
                          />
                        ) : item.logoBlack ? (
                          <Logo
                            variant={item.logoBlack}
                            key={_index}
                            title={item.title}
                            className={classnames(
                              'PlayNumber__label-logo',
                              `PlayNumber__label-logo--${item.logoBlack}`
                            )}
                          />
                        ) : (
                          item.title
                        )}
                      </span>
                      {_index !== label.length - 1 && <span>+</span>}
                    </React.Fragment>
                  ))}
                </span>
              </div>
            );
          })}
        </div>

        <Button
          variant={Button.Variant.Text}
          onClick={this.randomize}
          className="PlayNumber__button"
          ariaLabel="Neue Losnummer per Zufall generieren"
        >
          <Icon variant={IconVariant.Shuffle} size={IconSize.Large} />
          <span className="h-hide-xxs--down">Zufall</span>
        </Button>
      </div>
    );
  }
}

export default PlayNumber;
